import React from "react";

interface SummaryPoint {
  point: string;
  icon: string;
  highlight: string;
}

interface SlideContent {
  title?: string;
  subtitle?: string;
  headline?: string;
  summaryPoints?: string | SummaryPoint[];
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const defaultSummaryPoints: SummaryPoint[] = [
  {
    point: "",
    icon: "🎯",
    highlight: "",
  },
  {
    point: "",
    icon: "💡",
    highlight: "",
  },
  {
    point: "",
    icon: "⭐",
    highlight: "",
  },
];

const EditableDiv: React.FC<{
  text: string;
  onEdit: (value: string) => void;
  className?: string;
}> = ({ text, onEdit, className = "" }) => {
  return (
    <div
      contentEditable
      suppressContentEditableWarning
      onBlur={(e) => onEdit(e.currentTarget.textContent || "")}
      className={`outline-none ${className}`}
    >
      {text}
    </div>
  );
};

const SummarySlide = {
  id: "summary-slide",
  name: "Summary Slide",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
      defaultValue: "",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
      defaultValue: "",
    },
    {
      id: "headline",
      label: "Headline",
      type: "text",
      defaultValue: "",
    },
    {
      id: "summaryPoints",
      label: "Summary Points",
      type: "text",
      defaultValue: JSON.stringify(defaultSummaryPoints),
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue: "linear-gradient(135deg, #083344 0%, #155e75 100%)",
    },
  ],
  render: (content: SlideContent = {}) => {
    const {
      title = "",
      subtitle = "",
      headline = "",
      summaryPoints = defaultSummaryPoints,
      bgColor = "linear-gradient(135deg, #083344 0%, #155e75 100%)",
      onEdit = () => {},
    } = content;

    // Parse summary points from string if needed
    const parsedSummaryPoints = Array.isArray(summaryPoints)
      ? summaryPoints
      : typeof summaryPoints === "string"
        ? JSON.parse(summaryPoints)
        : defaultSummaryPoints;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    const handleSummaryPointEdit =
      (index: number, field: keyof SummaryPoint) => (value: string) => {
        const newSummaryPoints = [...parsedSummaryPoints];
        newSummaryPoints[index] = {
          ...newSummaryPoints[index],
          [field]: value,
        };
        handleEdit("summaryPoints")(JSON.stringify(newSummaryPoints));
      };

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/20 to-transparent" />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
              backgroundSize: "40px 40px",
            }}
          />
        </div>

        {/* Content Container */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full p-6">
          {/* Header */}
          <div className="text-center mb-6">
            <EditableDiv
              text={title}
              onEdit={handleEdit("title")}
              className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-cyan-200 to-cyan-100"
            />
            <EditableDiv
              text={subtitle}
              onEdit={handleEdit("subtitle")}
              className="text-xl text-cyan-100/80"
            />
          </div>

          {/* Headline */}
          <div className="text-center mb-8 max-w-3xl">
            <EditableDiv
              text={headline}
              onEdit={handleEdit("headline")}
              className="text-2xl font-semibold text-cyan-100"
            />
          </div>

          {/* Summary Points */}
          <div className="grid grid-cols-2 gap-4 max-w-5xl w-full px-6">
            {parsedSummaryPoints.map((point: SummaryPoint, idx: number) => (
              <div
                key={idx}
                className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-5 
                          transform hover:scale-[1.02] transition-all duration-300 hover:bg-white/20
                          border border-cyan-400/20"
              >
                <EditableDiv
                  text={point.icon}
                  onEdit={handleSummaryPointEdit(idx, "icon")}
                  className="text-3xl transform hover:scale-110 transition-transform"
                />
                <div className="flex-1 min-w-0 space-y-2">
                  <EditableDiv
                    text={point.point}
                    onEdit={handleSummaryPointEdit(idx, "point")}
                    className="text-lg font-medium text-cyan-100 leading-tight line-clamp-2"
                  />
                  <EditableDiv
                    text={point.highlight}
                    onEdit={handleSummaryPointEdit(idx, "highlight")}
                    className="text-sm text-cyan-200/80 leading-relaxed line-clamp-2"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Add some bottom spacing */}
          <div className="h-8" />
        </div>
      </div>
    );
  },
};

export default SummarySlide;
